import { useState, type ReactNode } from 'react';
import { ActionConfirmModal } from './ActionConfirmModal';
import { Tooltip } from '../../components/Tooltip';
import {
  hardDeleteBrief,
  softDeleteBrief,
} from '../../lib/partner-briefs-api';
import { zoneByCode } from '../../lib/zones';
import { toastFromError, useToast } from '../../components/ToastProvider';

/**
 * DeleteBriefModal — shared delete dialog used by the queue row trash
 * icon AND the Brief-detail DangerZone. Offers two side-by-side options:
 *
 *   Soft delete (recoverable, recommended default)
 *     - super_admin role
 *     - sets status='deleted', hidden from queue, restorable via Show
 *       Deleted toggle + Restore button
 *     - DDB row + S3 artefacts retained indefinitely (no auto-purge —
 *       operator runs Hard Delete from the Show Deleted view when truly
 *       done)
 *
 *   Hard delete (permanent, second-confirm)
 *     - super_admin role (no fresh-auth — body-confirm token + the
 *       second-confirm modal provide the friction)
 *     - removes DDB METADATA row + every S3 object under
 *       partner-briefs/{id}/. No recovery.
 *     - Always enabled — the second-confirm modal is the gate.
 *
 * UX intent: trust the operator. Both buttons available at all times;
 * the secondary confirm modal prevents accidental clicks without
 * blocking deliberate ones.
 */

interface DeleteBriefModalProps {
  open: boolean;
  applicationId: string;
  businessName: string;
  /**
   * ADR-122 Pattern 2 — the brief's own zone code, carried from the row or the detail page.
   *
   * ⚠ MATTERS MOST ON THIS COMPONENT OF ALL OF THEM. A hard delete is irreversible, so a
   * delete routed to the wrong zone is either a 404 the operator reads as "already gone" or,
   * far worse, nothing at all while they believe the record was purged. Omitted resolves to
   * the home zone, which is where every delete went before ADR-122.
   */
  zone?: string;
  onDeleted: () => void;
  onCancel: () => void;
}

export function DeleteBriefModal({
  open,
  applicationId,
  businessName,
  zone,
  onDeleted,
  onCancel,
}: DeleteBriefModalProps) {
  const targetZone = zoneByCode(zone);
  const toast = useToast();
  const [mode, setMode] = useState<'choose' | 'soft-loading' | 'hard-confirm' | 'hard-loading'>('choose');
  const [error, setError] = useState<string | null>(null);

  async function runSoft() {
    setMode('soft-loading');
    setError(null);
    try {
      await softDeleteBrief(applicationId, targetZone);
      toast.push({
        variant: 'success',
        title: `Soft-deleted: ${businessName}`,
        detail: 'Recoverable via Show deleted + Restore.',
      });
      onDeleted();
      setMode('choose');
    } catch (err) {
      const t = toastFromError(err, `Could not delete ${businessName}`);
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
      setError(err instanceof Error ? err.message : 'Soft delete failed.');
      setMode('choose');
    }
  }

  function startHard() {
    setError(null);
    setMode('hard-confirm');
  }

  async function runHard() {
    setMode('hard-loading');
    setError(null);
    try {
      await hardDeleteBrief(applicationId, targetZone);
      toast.push({
        variant: 'success',
        title: `Permanently deleted: ${businessName}`,
        detail: 'DDB row + S3 artefacts purged. No recovery.',
      });
      onDeleted();
      setMode('choose');
    } catch (err) {
      const t = toastFromError(err, `Hard delete failed for ${businessName}`);
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
      setError(err instanceof Error ? err.message : 'Hard delete failed.');
      setMode('choose');
    }
  }

  const busy = mode === 'soft-loading' || mode === 'hard-loading';
  // Body description stripped 30 May 2026 per operator UAT — the Soft +
  // Hard delete buttons each carry their own tooltip with the full
  // explanation, so duplicating it in the body just adds noise.
  const body: ReactNode = (
    <>
      <p>
        Delete the Brief for <strong>{businessName}</strong>? Choose{' '}
        <strong>Soft delete</strong> (recoverable) or <strong>Hard delete</strong>{' '}
        (permanent) — hover either action for details.
      </p>
      {error && <div className="portal-error" role="alert">{error}</div>}
    </>
  );

  return (
    <>
      <ActionConfirmModal
        open={open && mode !== 'hard-confirm'}
        title={`Delete Brief — ${businessName}`}
        intent="danger"
        primaryLabel={mode === 'soft-loading' ? 'Soft-deleting…' : 'Soft delete'}
        loading={busy}
        onConfirm={() => void runSoft()}
        onCancel={() => {
          setError(null);
          setMode('choose');
          onCancel();
        }}
        primaryTooltip={{
          title: 'Soft delete · recoverable',
          variant: 'success',
          aria: 'Soft delete: status moves to deleted, Brief hidden from queue, recoverable via Show deleted + Restore. DDB row and S3 artefacts retained.',
          content: (
            <>
              Status moves to <code>deleted</code> and the Brief is hidden from the
              queue + Active Partners views. Recoverable via <strong>Show deleted</strong>
              {' '}+ <strong>Restore</strong>. DDB row + S3 artefacts retained — no auto-purge.
            </>
          ),
        }}
        extraAction={
          <Tooltip
            title="Hard delete · permanent"
            variant="danger"
            anchorClassName="portal-modal-actions-hard-delete-anchor"
            aria="Hard delete: permanently removes DDB row and S3 artefact triad. No recovery. A confirmation prompt fires before action."
            content={
              <>
                Permanently removes the DDB row + S3 artefact triad
                {' '}(<code>evidence-dossier</code> + <code>score-breakdown</code> +{' '}
                <code>agent-trace</code>). <strong>No recovery.</strong> A confirmation
                prompt fires before the action.
              </>
            }
          >
            <button
              type="button"
              className="portal-link-button-secondary portal-modal-actions-hard-delete"
              onClick={startHard}
              disabled={busy}
            >
              {mode === 'hard-loading' ? 'Hard-deleting…' : 'Hard delete'}
            </button>
          </Tooltip>
        }
      >
        {body}
      </ActionConfirmModal>
      {/* Secondary confirmation — fires only when operator clicks Hard delete */}
      <ActionConfirmModal
        open={open && mode === 'hard-confirm'}
        title="Permanently delete this Brief?"
        intent="danger"
        primaryLabel="Yes, permanently delete"
        loading={mode === 'hard-loading'}
        onConfirm={() => void runHard()}
        onCancel={() => setMode('choose')}
      >
        <p>
          You are about to permanently delete the Brief for{' '}
          <strong>{businessName}</strong>.
        </p>
        <p>
          This removes the DDB METADATA row and every S3 object under{' '}
          <code>partner-briefs/{applicationId}/</code>. <strong>All data will be lost.</strong>{' '}
          There is no recovery.
        </p>
        <p>Click <strong>Yes, permanently delete</strong> to proceed, or Cancel to keep the Brief.</p>
      </ActionConfirmModal>
    </>
  );
}
